/**
 * NavIcon — renders the Hugeicons equivalent of a lucide nav icon.
 *
 * The navigation config (navigationConfig.ts) stores lucide components. This
 * maps each one to its DB-consistent Hugeicons (free set) counterpart so the
 * module sidebar + submenus render Hugeicons, without rewriting ~60 config
 * entries. Any lucide icon not in the map falls back to rendering lucide.
 *
 * className carries the size (e.g. w-[17px] h-[17px]) and color — forwarded to
 * both renderers; CSS width/height win over Hugeicons' size attribute.
 */
import type { LucideIcon } from "lucide-react";
import {
  ArrowRightLeft,
  Award,
  BadgePercent,
  BarChart3,
  Boxes,
  Building2,
  Calculator,
  CalendarClock,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CreditCard,
  DollarSign,
  Download,
  Factory,
  FileCheck,
  FileImage,
  FilePlus2,
  FileSpreadsheet,
  FileText,
  Gift,
  Globe,
  Handshake,
  Hash,
  Home,
  Inbox,
  Layers,
  Layers3,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  ListTree,
  Mail,
  MapPin,
  MapPinned,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Monitor,
  Newspaper,
  Package,
  PackageCheck,
  PackageOpen,
  PackageSearch,
  Palette,
  Percent,
  PieChart,
  Printer,
  Quote,
  RefreshCw,
  RotateCcw,
  Route,
  Scale,
  ScrollText,
  Search,
  Settings,
  Ship,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  Shuffle,
  SlidersHorizontal,
  Smartphone,
  Star,
  Store,
  Tag,
  Target,
  Ticket,
  TrendingUp,
  Truck,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  UsersRound,
  Warehouse,
  Workflow,
  Zap,
} from "lucide-react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  ArrowDown01Icon,
  ArrowTurnBackwardIcon,
  Award01Icon,
  BoatIcon,
  Building03Icon,
  BubbleChatIcon,
  CalculatorIcon,
  Calendar03Icon,
  ChartColumnIcon,
  ChartUpIcon,
  CheckmarkSquare01Icon,
  ComputerIcon,
  ContactIcon,
  CreditCardIcon,
  CubeIcon,
  Csv01Icon,
  DashboardSquare01Icon,
  DeliveryBox01Icon,
  Dollar01Icon,
  Download04Icon,
  FactoryIcon,
  File01Icon,
  FileAddIcon,
  FlashIcon,
  GiftIcon,
  GlobeIcon,
  GridViewIcon,
  HandshakeIcon,
  HashIcon,
  Home01Icon,
  Image01Icon,
  InboxIcon,
  Layers01Icon,
  Link01Icon,
  ListViewIcon,
  Location01Icon,
  Location04Icon,
  Mail01Icon,
  Megaphone01Icon,
  Message01Icon,
  News01Icon,
  PackageDeliveredIcon,
  PackageIcon,
  PackageSearchIcon,
  PaintBoardIcon,
  PercentIcon,
  PieChartIcon,
  PrinterIcon,
  QuoteDownIcon,
  RefreshIcon,
  Route01Icon,
  SaleTag01Icon,
  Search01Icon,
  Settings01Icon,
  ShoppingBag01Icon,
  ShoppingBasket01Icon,
  ShoppingCart01Icon,
  ShuffleIcon,
  SlidersHorizontalIcon,
  SmartPhone01Icon,
  StarIcon,
  Store01Icon,
  Tag01Icon,
  Target01Icon,
  TaskDone01Icon,
  Task01Icon,
  Ticket01Icon,
  TruckIcon,
  Upload04Icon,
  UserAdd01Icon,
  UserCheck01Icon,
  UserGroupIcon,
  UserMultiple02Icon,
  WarehouseIcon,
  WeightScaleIcon,
  WorkflowSquare01Icon,
} from "@hugeicons/core-free-icons";

/** lucide component → Hugeicons (free) equivalent. */
const LUCIDE_TO_HUGE = new Map<LucideIcon, IconSvgElement>([
  [ArrowRightLeft, ArrowDataTransferHorizontalIcon],
  [Award, Award01Icon],
  [BadgePercent, SaleTag01Icon],
  [BarChart3, ChartColumnIcon],
  [Boxes, CubeIcon],
  [Building2, Building03Icon],
  [Calculator, CalculatorIcon],
  [CalendarClock, Calendar03Icon],
  [ChevronDown, ArrowDown01Icon],
  [ClipboardCheck, TaskDone01Icon],
  [ClipboardList, Task01Icon],
  [Contact, ContactIcon],
  [CreditCard, CreditCardIcon],
  [DollarSign, Dollar01Icon],
  [Download, Download04Icon],
  [Factory, FactoryIcon],
  [FileCheck, CheckmarkSquare01Icon],
  [FileImage, Image01Icon],
  [FilePlus2, FileAddIcon],
  [FileSpreadsheet, Csv01Icon],
  [FileText, File01Icon],
  [Gift, GiftIcon],
  [Globe, GlobeIcon],
  [Handshake, HandshakeIcon],
  [Hash, HashIcon],
  [Home, Home01Icon],
  [Inbox, InboxIcon],
  [Layers, Layers01Icon],
  [Layers3, Layers01Icon],
  [LayoutDashboard, DashboardSquare01Icon],
  [LayoutGrid, GridViewIcon],
  [Link2, Link01Icon],
  [ListTree, ListViewIcon],
  [Mail, Mail01Icon],
  [MapPin, Location01Icon],
  [MapPinned, Location04Icon],
  [Megaphone, Megaphone01Icon],
  [MessageCircle, BubbleChatIcon],
  [MessageSquare, Message01Icon],
  [Monitor, ComputerIcon],
  [Newspaper, News01Icon],
  [Package, PackageIcon],
  [PackageCheck, PackageDeliveredIcon],
  [PackageOpen, DeliveryBox01Icon],
  [PackageSearch, PackageSearchIcon],
  [Palette, PaintBoardIcon],
  [Percent, PercentIcon],
  [PieChart, PieChartIcon],
  [Printer, PrinterIcon],
  [Quote, QuoteDownIcon],
  [RefreshCw, RefreshIcon],
  [RotateCcw, ArrowTurnBackwardIcon],
  [Route, Route01Icon],
  [Scale, WeightScaleIcon],
  [ScrollText, File01Icon],
  [Search, Search01Icon],
  [Settings, Settings01Icon],
  [Ship, BoatIcon],
  [ShoppingBag, ShoppingBag01Icon],
  [ShoppingBasket, ShoppingBasket01Icon],
  [ShoppingCart, ShoppingCart01Icon],
  [Shuffle, ShuffleIcon],
  [SlidersHorizontal, SlidersHorizontalIcon],
  [Smartphone, SmartPhone01Icon],
  [Star, StarIcon],
  [Store, Store01Icon],
  [Tag, Tag01Icon],
  [Target, Target01Icon],
  [Ticket, Ticket01Icon],
  [TrendingUp, ChartUpIcon],
  [Truck, TruckIcon],
  [Upload, Upload04Icon],
  [UserCheck, UserCheck01Icon],
  [UserPlus, UserAdd01Icon],
  [Users, UserGroupIcon],
  [UsersRound, UserMultiple02Icon],
  [Warehouse, WarehouseIcon],
  [Workflow, WorkflowSquare01Icon],
  [Zap, FlashIcon],
]);

interface NavIconProps {
  icon: LucideIcon;
  className?: string;
  strokeWidth?: number;
}

/** Render a nav icon as Hugeicons when mapped, else fall back to lucide. */
export function NavIcon({ icon: Icon, className, strokeWidth = 1.5 }: NavIconProps) {
  const huge = LUCIDE_TO_HUGE.get(Icon);
  if (huge) {
    return <HugeiconsIcon icon={huge} className={className} strokeWidth={strokeWidth} />;
  }
  return <Icon className={className} strokeWidth={strokeWidth} />;
}
